/**
 * Free Fins Offer V2
 * Custom Element: <free-fins-offer>
 */

class FreeFinsOffer extends HTMLElement {
  connectedCallback() {
    this.boardHandle = this.dataset.boardHandle;
    this.boardVariantId = parseInt(this.dataset.boardVariantId, 10);
    this.mappingsUrl = this.dataset.mappingsUrl;
    this.discountCode = this.dataset.discountCode || 'FINS_PACK';
    this.currency = this.dataset.currency || 'EUR';

    this.t = {};
    for (const [key, value] of Object.entries(this.dataset)) {
      if (key.startsWith('t')) {
        const tKey = key.charAt(1).toLowerCase() + key.slice(2);
        this.t[tKey] = value;
      }
    }

    this.selectedFin = null;
    this.optionsContainer = this.querySelector('.free-fins-offer__options');
    this.loadingEl = this.querySelector('.free-fins-offer__loading');
    this.noFinsEl = this.querySelector('.free-fins-offer__no-fins');
    this.savingsEl = this.querySelector('.free-fins-offer__savings');
    this.savingsLabel = this.querySelector('.free-fins-offer__savings-label');
    this.savingsAmount = this.querySelector('.free-fins-offer__savings-amount');
    this.cta = this.querySelector('.free-fins-offer__cta');
    this.ctaText = this.querySelector('.free-fins-offer__cta-text');
    this.ctaSpinner = this.querySelector('.free-fins-offer__cta-spinner');
    this.messageEl = this.querySelector('.free-fins-offer__message');

    this.setText('.free-fins-offer__title', this.t.title || '');
    this.setText('.free-fins-offer__subtitle', this.t.subtitle || '');
    if (this.loadingEl) this.loadingEl.textContent = this.t.loading || '';
    if (this.noFinsEl) this.noFinsEl.textContent = this.t.noFins || '';
    if (this.savingsLabel) this.savingsLabel.textContent = this.t.savings || '';
    if (this.ctaText) this.ctaText.textContent = this.t.addToCart || '';

    if (this.cta) {
      this.cta.addEventListener('click', () => this.addToCart());
    }

    this.initialize();
  }

  static loadMappings(url) {
    if (!window.__finMappingsCache) {
      window.__finMappingsCache = FreeFinsOffer.fetchWithTimeout(url)
        .then(res => {
          if (!res.ok) throw new Error('Failed to load fin mappings');
          return res.json();
        })
        .catch(err => {
          window.__finMappingsCache = null;
          throw err;
        });
    }
    return window.__finMappingsCache;
  }

  static fetchWithTimeout(url, opts = {}, ms = 5000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return fetch(url, { ...opts, signal: controller.signal }).finally(() => clearTimeout(timer));
  }

  async initialize() {
    try {
      const mappings = await FreeFinsOffer.loadMappings(this.mappingsUrl);
      if (!mappings) return;

      const bundles = this.getBundles(mappings);
      if (bundles.length === 0) {
        this.showNoFins();
        return;
      }

      const uniqueHandles = [...new Set(bundles.flat())];
      const productMap = await this.loadFinProducts(uniqueHandles);

      this.renderOptions(bundles, productMap);
    } catch (err) {
      if (err.name === 'AbortError') {
        this.showMessage(this.t.timeout || 'Loading took too long.', 'error');
      } else {
        this.showMessage(this.t.error || 'Error loading fins.', 'error');
      }
      if (this.loadingEl) this.loadingEl.hidden = true;
    }
  }

  getBundles(mappings) {
    const boardData = mappings[this.boardHandle];
    if (!boardData) return [];

    const seen = new Set();
    const bundles = [];
    for (const construction of Object.values(boardData)) {
      for (const bundle of construction) {
        const key = JSON.stringify(bundle);
        if (!seen.has(key)) {
          seen.add(key);
          bundles.push(bundle);
        }
      }
    }
    return bundles;
  }

  async loadFinProducts(handles) {
    const productMap = {};
    const results = await Promise.allSettled(
      handles.map(handle =>
        FreeFinsOffer.fetchWithTimeout(`/products/${handle}.js`)
          .then(res => (res.ok ? res.json() : null))
          .catch(() => null)
      )
    );
    results.forEach((result, i) => {
      const product = result.status === 'fulfilled' ? result.value : null;
      // Keep out-of-stock fins so the offer stays visible; they are flagged at render time.
      if (product) {
        productMap[handles[i]] = product;
      }
    });
    return productMap;
  }

  renderOptions(bundles, productMap) {
    const validBundles = bundles.filter(bundle =>
      bundle.every(handle => productMap[handle])
    );

    if (validBundles.length === 0) {
      this.showNoFins();
      return;
    }

    if (this.loadingEl) this.loadingEl.hidden = true;

    // In-stock bundles first; sort is stable so the mapping order is otherwise kept.
    const inStock = bundle => bundle.every(handle => productMap[handle].available);
    validBundles.sort((a, b) => Number(inStock(b)) - Number(inStock(a)));

    validBundles.forEach((bundle, index) => {
      const el = this.renderOption(bundle, productMap, index);
      if (el) this.optionsContainer.appendChild(el);
    });

    this.removeAttribute('hidden');

    const firstOption = this.optionsContainer.querySelector('.free-fins-offer__option');
    if (firstOption) this.selectOption(firstOption);
  }

  renderOption(bundle, productMap, index) {
    const products = bundle.map(h => productMap[h]).filter(Boolean);
    if (products.length === 0) return null;

    const totalPrice = products.reduce((sum, p) => {
      const v = p.variants && p.variants[0];
      return sum + (v ? v.price : 0);
    }, 0);

    const variantIds = products.map(p => p.variants && p.variants[0] && p.variants[0].id).filter(Boolean);
    const availableIds = products
      .filter(p => p.available)
      .map(p => p.variants && p.variants[0] && p.variants[0].id)
      .filter(Boolean);
    const isOutOfStock = availableIds.length < variantIds.length;
    const bundleTitle = this.buildBundleTitle(products);

    const label = document.createElement('label');
    label.className = 'free-fins-offer__option';
    label.setAttribute('role', 'radio');
    label.setAttribute('aria-checked', 'false');
    label.setAttribute('tabindex', '0');

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'free-fins-' + this.boardHandle;
    radio.className = 'free-fins-offer__radio';
    radio.dataset.finIds = variantIds.join(',');
    radio.dataset.finAvailableIds = availableIds.join(',');
    radio.dataset.finHandles = bundle.join(',');
    radio.dataset.finPrice = String(totalPrice);
    radio.dataset.bundleSize = String(products.length);
    radio.setAttribute('aria-hidden', 'true');
    radio.tabIndex = -1;

    const content = document.createElement('div');
    content.className = 'free-fins-offer__option-content';

    const firstProduct = products[0];
    const image = firstProduct.featured_image || (firstProduct.images && firstProduct.images[0]);
    if (image) {
      const imgWrapper = document.createElement('div');
      imgWrapper.className = 'free-fins-offer__fin-image-wrapper';

      const img = document.createElement('img');
      const imgUrl = image.includes('cdn.shopify.com')
        ? image.replace(/(\.[a-z]{3,4})(\?|$)/i, '_120x120$1$2')
        : image;
      img.src = imgUrl;
      img.alt = bundleTitle;
      img.className = 'free-fins-offer__fin-image';
      img.loading = 'lazy';
      img.width = 56;
      img.height = 56;
      imgWrapper.appendChild(img);
      content.appendChild(imgWrapper);
    }

    const details = document.createElement('div');
    details.className = 'free-fins-offer__fin-details';

    const titleSpan = document.createElement('span');
    titleSpan.className = 'free-fins-offer__fin-title';
    titleSpan.textContent = bundleTitle;
    details.appendChild(titleSpan);

    const priceRow = document.createElement('div');
    priceRow.className = 'free-fins-offer__fin-price';

    const originalPrice = document.createElement('span');
    originalPrice.className = 'free-fins-offer__fin-price-original';
    originalPrice.textContent = this.formatMoney(totalPrice);
    priceRow.appendChild(originalPrice);

    const badge = document.createElement('span');
    badge.className = 'free-fins-offer__badge';
    if (products.length > 1) {
      badge.textContent = (this.t.badgeMulti || '{{ count }} FINS FREE').replace('{{ count }}', String(products.length));
    } else {
      badge.textContent = this.t.badge || 'FREE';
    }
    priceRow.appendChild(badge);

    details.appendChild(priceRow);

    if (isOutOfStock) {
      label.classList.add('free-fins-offer__option--out-of-stock');
      const stockNote = document.createElement('span');
      stockNote.className = 'free-fins-offer__fin-stock';
      stockNote.textContent = this.t.outOfStock || 'Temporarily out of stock';
      details.appendChild(stockNote);
    }

    content.appendChild(details);

    label.appendChild(radio);
    label.appendChild(content);

    label.addEventListener('click', (e) => {
      e.preventDefault();
      this.selectOption(label);
    });

    label.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        this.selectOption(label);
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        const next = label.nextElementSibling;
        if (next && next.classList.contains('free-fins-offer__option')) {
          next.focus();
          this.selectOption(next);
        }
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = label.previousElementSibling;
        if (prev && prev.classList.contains('free-fins-offer__option')) {
          prev.focus();
          this.selectOption(prev);
        }
      }
    });

    return label;
  }

  buildBundleTitle(products) {
    if (products.length === 1) return products[0].title || '';
    return products.map(p => {
      const title = (p.title || '').toLowerCase();
      if (title.includes('central')) {
        const colorMatch = title.match(/blue|green|purple|black|bleu|vert|violet|noir/i);
        return 'Central ' + (colorMatch ? colorMatch[0].charAt(0).toUpperCase() + colorMatch[0].slice(1) : '');
      }
      if (title.includes('lateraux') || title.includes('lat\u00e9raux')) {
        return 'Lat\u00e9raux';
      }
      return (p.title || '').split(' ').slice(0, 2).join(' ');
    }).join(' + ');
  }

  selectOption(optionEl) {
    const allOptions = this.optionsContainer.querySelectorAll('.free-fins-offer__option');
    allOptions.forEach(o => {
      o.setAttribute('aria-checked', 'false');
      o.setAttribute('tabindex', '-1');
      const r = o.querySelector('input[type="radio"]');
      if (r) r.checked = false;
    });

    optionEl.setAttribute('aria-checked', 'true');
    optionEl.setAttribute('tabindex', '0');
    const radio = optionEl.querySelector('input[type="radio"]');
    if (radio) {
      radio.checked = true;
      const ids = radio.dataset.finIds.split(',').map(id => parseInt(id, 10));
      const handles = radio.dataset.finHandles.split(',');
      const availableIds = (radio.dataset.finAvailableIds || '')
        .split(',').filter(Boolean).map(id => parseInt(id, 10));
      this.selectedFin = {
        ids: ids,
        availableIds: availableIds,
        handles: handles,
        price: parseInt(radio.dataset.finPrice, 10),
        bundleSize: parseInt(radio.dataset.bundleSize, 10)
      };
    }

    this.updateSavings();
    if (this.cta) this.cta.disabled = false;
    this.hideMessage();
  }

  updateSavings() {
    if (this.selectedFin && this.savingsEl && this.savingsAmount) {
      this.savingsEl.hidden = false;
      this.savingsAmount.textContent = this.formatMoney(this.selectedFin.price);
    }
  }

  async addToCart() {
    if (!this.selectedFin || !this.selectedFin.ids.length) {
      this.showMessage(this.t.selectFin || 'Please select a fin option', 'error');
      return;
    }

    this.setCTALoading(true);
    this.hideMessage();

    try {
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cartRes = await FreeFinsOffer.fetchWithTimeout(root + 'cart.js');
      if (!cartRes.ok) throw new Error('Cart fetch failed');
      const cart = await cartRes.json();

      const boardInCart = cart.items && cart.items.some(item => item.variant_id === this.boardVariantId);
      const finsNotInCart = this.selectedFin.availableIds.filter(finId =>
        !(cart.items && cart.items.some(item => item.variant_id === finId))
      );

      const itemsToAdd = [];
      if (!boardInCart) {
        itemsToAdd.push({ id: this.boardVariantId, quantity: 1 });
      }
      finsNotInCart.forEach(finId => {
        itemsToAdd.push({ id: finId, quantity: 1 });
      });

      if (itemsToAdd.length === 0) {
        this.showMessage(this.t.already || 'Already in cart', 'error');
        this.setCTALoading(false);
        return;
      }

      const addRes = await FreeFinsOffer.fetchWithTimeout(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      if (!addRes.ok) {
        const errData = await addRes.json().catch(() => ({}));
        throw new Error(errData.description || this.t.error || 'Error adding to cart');
      }

      this.showMessage(this.t.success || 'Added to cart!', 'success');

      // Out-of-stock fins are not added, so only apply the FINS_PACK discount when fins are in the cart.
      const hasFins = this.selectedFin.availableIds.length > 0;
      setTimeout(() => {
        const redirectCart = encodeURIComponent(root + 'cart');
        window.location.href = hasFins
          ? root + 'discount/' + encodeURIComponent(this.discountCode) + '?redirect=' + redirectCart
          : root + 'cart';
      }, 1500);

    } catch (err) {
      if (err.name === 'AbortError') {
        this.showMessage(this.t.timeout || 'Request timed out', 'error');
      } else {
        this.showMessage(err.message || this.t.error || 'Error', 'error');
      }
      this.setCTALoading(false);
    }
  }

  setCTALoading(loading) {
    if (!this.cta) return;
    this.cta.disabled = loading;
    if (this.ctaText) {
      this.ctaText.textContent = loading
        ? (this.t.adding || 'Adding...')
        : (this.t.addToCart || 'Add to cart');
    }
    if (this.ctaSpinner) this.ctaSpinner.hidden = !loading;
  }

  showNoFins() {
    if (this.loadingEl) this.loadingEl.hidden = true;
    if (this.noFinsEl) this.noFinsEl.hidden = false;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.currency
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2) + ' ' + this.currency;
    }
  }

  showMessage(text, type) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.className = 'free-fins-offer__message free-fins-offer__message--' + type;
    this.messageEl.hidden = false;
  }

  hideMessage() {
    if (!this.messageEl) return;
    this.messageEl.textContent = '';
    this.messageEl.className = 'free-fins-offer__message';
    this.messageEl.hidden = true;
  }

  setText(selector, text) {
    const el = this.querySelector(selector);
    if (el) el.textContent = text;
  }
}

customElements.define('free-fins-offer', FreeFinsOffer);
